import type { Vector2 } from 'utils/Vector2'
import {
  normalize,
  subtract,
  dot,
  length,
  add,
  multiplyScalar,
  rotate,
} from 'utils/Vector2'

import type { Box } from 'EditorApp/types'

interface GradientStop {
  color: string
  offset: string
}

interface Gradient {
  degrees: number
  stops: GradientStop[]
}

export const parseCssGradient = (cssGradient: string): Gradient => {
  const [angle, ...cssStops] = cssGradient.split(',').map((t) => t.trim())

  const stops = cssStops.map((stop) => {
    const [color, offset] = stop.split(/\s+/).map((t) => t.trim())
    return { color, offset }
  })

  return {
    degrees: parseInt(angle, 10) || 0,
    stops,
  }
}

const nearestPointOnLine = (
  linePoint: Vector2,
  lineDirection: Vector2,
  point: Vector2
) => {
  const dir = normalize(lineDirection)

  const offset = subtract(point, linePoint)
  const delta = dot(offset, dir)

  return add(linePoint, multiplyScalar(dir, delta))
}

// To visualise what we are doing here, see the diagram on MDN:
// https://developer.mozilla.org/en-US/docs/Web/CSS/gradient/linear-gradient#composition_of_a_linear_gradient
//
// We can't just simply rotate the gradient axis, because the start and end
// points are extended beyond the region in order to create "magic corners".
export const gradientStartAndEnd = (angle: number, box: Box) => {
  const right = box.left + box.width
  const bottom = box.top + box.height

  // Center of the region rectangle.
  const center = {
    x: box.left + box.width / 2,
    y: box.top + box.height / 2,
  }

  // Direction of the gradient axis.
  const direction = rotate({ x: 0, y: 1 }, angle)

  // Each of the corners of the region, stored as vectors.
  const corners = [
    { x: box.left, y: box.top },
    { x: right, y: box.top },
    { x: right, y: bottom },
    { x: box.left, y: bottom },
  ]

  // Select the 2 corners in same hemisphere as gradient axis
  const [c1, c2] = corners.filter((corner) => {
    return dot(subtract(corner, center), direction) >= 0
  })

  // Project each corner onto the gradient axis, then pick the one that is
  // closest to the corner it came from.
  const p1 = nearestPointOnLine(center, direction, c1)
  const p2 = nearestPointOnLine(center, direction, c2)

  const d1 = length(subtract(p1, c1))
  const d2 = length(subtract(p2, c2))

  const startPoint = d1 < d2 ? p1 : p2
  const endPoint = subtract(center, subtract(startPoint, center))

  return [startPoint, endPoint]
}
